import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { io, type Socket } from 'socket.io-client';
import { useToast } from '../components/ui/Toast';
import { useAuth } from '../features/auth/AuthProvider';
import { apiOrigin, getAccessToken, refreshSession } from './http';

/**
 * Live updates (Phase 8, docs/api/11). The socket only says *what changed* (ids and versions); every
 * screen then refetches through REST, which applies permissions. If the socket is down the app still works:
 * queries refetch on focus and reconnect as before.
 */
export type RealtimeStatus = 'off' | 'connecting' | 'live' | 'offline';

interface RealtimeContextValue {
  status: RealtimeStatus;
  socket: Socket | null;
}

const RealtimeContext = createContext<RealtimeContextValue>({ status: 'off', socket: null });

type Payload = Record<string, unknown>;

/** Collapses bursts of events into one invalidation per query key family. */
function batcher(queryClient: QueryClient) {
  const pending = new Set<string>();
  let timer: ReturnType<typeof setTimeout> | null = null;
  return (...keys: unknown[][]) => {
    for (const k of keys) pending.add(JSON.stringify(k));
    timer ??= setTimeout(() => {
      timer = null;
      const batch = [...pending];
      pending.clear();
      for (const k of batch) void queryClient.invalidateQueries({ queryKey: JSON.parse(k) as unknown[] });
    }, 400);
  };
}

/** What each server event refreshes (query keys from features/leads/api.ts and alerts). */
export function keysFor(event: string, payload: Payload): unknown[][] {
  const leadId = typeof payload.leadId === 'string' ? payload.leadId : null;
  switch (event) {
    case 'lead.created':
      return [['leads']];
    case 'lead.changed':
      return [['leads'], ['follow-ups'], ['alerts'], ['transfer-requests'], ['workload']];
    case 'activity.created':
      return leadId
        ? [
            ['leads', 'activities', leadId],
            ['leads', 'detail', leadId],
          ]
        : [];
    case 'followup.changed':
      return [['follow-ups'], ...(leadId ? [['leads', 'detail', leadId]] : [])];
    case 'assignment.created':
    case 'assignment.acknowledged':
      return [['leads'], ['alerts'], ['workload']];
    case 'transfer.requested':
    case 'transfer.decided':
      return [['transfer-requests'], ['leads']];
    default:
      return [];
  }
}

export function RealtimeProvider({ children }: { children: ReactNode }) {
  const { me, reload } = useAuth();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [status, setStatus] = useState<RealtimeStatus>('connecting');
  const toastRef = useRef(toast);
  useEffect(() => {
    toastRef.current = toast;
  });
  const tenantUser = me?.user.type === 'tenant' ? me.user.id : null;
  // One socket per signed-in tenant user; it connects in the effect below.
  const socket = useMemo(
    () =>
      tenantUser
        ? io(apiOrigin(), {
            path: '/socket.io',
            autoConnect: false,
            transports: ['websocket', 'polling'],
            // Called on every (re)connect, so a refreshed token is always used.
            auth: (cb) => cb({ token: getAccessToken() ?? '' }),
            reconnectionDelay: 1000,
            reconnectionDelayMax: 30_000,
          })
        : null,
    [tenantUser],
  );

  useEffect(() => {
    if (!socket) return;
    const s = socket;
    const invalidate = batcher(queryClient);
    s.on('ready', () => {
      setStatus('live');
      // Anything missed while disconnected is picked up by refetching what is on screen.
      void queryClient.invalidateQueries({ type: 'active' });
    });
    s.on('disconnect', () => setStatus('offline'));
    s.on('connect_error', (err: Error & { data?: { code?: string } }) => {
      setStatus('offline');
      // An expired access token: refresh through REST, then retry with the new one.
      if (err.data?.code === 'UNAUTHENTICATED') void refreshSession().then((ok) => ok && s.connect());
    });
    s.on('session.expired', () => void refreshSession().then((ok) => ok && (s.disconnect(), s.connect())));
    s.on('session.refresh', () => {
      void reload();
      setTimeout(() => s.connect(), 500);
    });
    s.on('assignment.created', () => toastRef.current.success('A new lead was assigned to you'));
    s.onAny((event: string, payload: Payload) => {
      const keys = keysFor(event, payload ?? {});
      if (keys.length) invalidate(...keys);
    });
    s.connect();
    return () => {
      s.removeAllListeners();
      s.disconnect();
    };
  }, [socket, queryClient, reload]);

  return (
    <RealtimeContext.Provider value={{ status: socket ? status : 'off', socket }}>
      {children}
    </RealtimeContext.Provider>
  );
}

export function useRealtimeStatus(): RealtimeStatus {
  return useContext(RealtimeContext).status;
}

/** Joins the live room of one lead while a screen shows it (docs/api/11 §2, screen-scoped). */
export function useLeadLive(leadId: string | undefined): void {
  const { socket, status } = useContext(RealtimeContext);
  useEffect(() => {
    if (!socket || !leadId || status !== 'live') return;
    socket.emit('subscribe', { resource: 'lead', id: leadId });
    return () => {
      socket.emit('unsubscribe', { resource: 'lead', id: leadId });
    };
  }, [socket, leadId, status]);
}
